import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
	ArrowLeft,
	Check,
	Eye,
	EyeOff,
	KeyRound,
	Lock,
	ShieldCheck,
	X,
} from "lucide-react";
import { HelmLogo } from "@/components/helm-logo";
import { useCallback, useMemo, useState } from "react";
import z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { authService } from "@/lib/auth-service";
import { trackUserSignedUp } from "@/lib/analytics-events";
import { API_BASE_URL } from "@/lib/consts";

// --- Route definition ---

export const Route = createFileRoute("/auth/create-password")({
	component: CreatePasswordPage,
	validateSearch: (search) =>
		z.object({ token: z.string().min(1) }).parse(search),
});

// --- Password validation ---

interface PasswordRequirement {
	label: string;
	test: (pw: string) => boolean;
}

const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
	{ label: "At least 8 characters", test: (pw) => pw.length >= 8 },
	{ label: "One uppercase letter", test: (pw) => /[A-Z]/.test(pw) },
	{ label: "One lowercase letter", test: (pw) => /[a-z]/.test(pw) },
	{ label: "One number", test: (pw) => /\d/.test(pw) },
	{
		label: "One special character",
		test: (pw) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(pw),
	},
];

function getStrength(password: string): {
	score: number;
	label: string;
	color: string;
} {
	if (!password) return { score: 0, label: "", color: "" };

	const passed = PASSWORD_REQUIREMENTS.filter((r) => r.test(password)).length;

	if (passed <= 2) return { score: 20, label: "Weak", color: "bg-red-500" };
	if (passed <= 3) return { score: 50, label: "Fair", color: "bg-amber-500" };
	if (passed <= 4) return { score: 75, label: "Good", color: "bg-blue-500" };
	return { score: 100, label: "Strong", color: "bg-green-500" };
}

// --- Main component ---

function CreatePasswordPage() {
	const { token } = Route.useSearch();
	const navigate = useNavigate();

	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const requirementResults = useMemo(
		() => PASSWORD_REQUIREMENTS.map((r) => ({ ...r, met: r.test(password) })),
		[password],
	)

	const allRequirementsMet = requirementResults.every((r) => r.met);
	const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
	const canSubmit = allRequirementsMet && passwordsMatch && !submitting;

	const strength = useMemo(() => getStrength(password), [password]);

	const handleSubmit = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			if (!canSubmit) return;

			setSubmitting(true);
			setError(null);

			try {
				const res = await fetch(`${API_BASE_URL}/auth/setup-password`, {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						invite_token: token,
						password,
						confirm_password: confirmPassword,
					}),
				})

				if (res.ok) {
					const data = await res.json().catch(() => null);
					if (data?.access_token) {
						authService.setTokens({
							authToken: data.access_token,
							refreshToken: data.refresh_token,
							expiry: data.expires_in ?? 1800,
						});
					}
					trackUserSignedUp("email");
					navigate({
						to: "/auth/phone-verification" as string,
						search: { token },
					})
					return
				}

				const body = await res.json().catch(() => null);
				const detail = body?.detail;

				if (detail && typeof detail === "object" && detail.message) {
					setError(detail.message);
				} else if (typeof detail === "string") {
					setError(detail);
				} else {
					setError("Something went wrong. Please try again.");
				}
			} catch {
				setError("Unable to connect. Please check your connection and try again.");
			} finally {
				setSubmitting(false);
			}
		},
		[canSubmit, token, password, confirmPassword, navigate],
	)

	return (
		<div className="min-h-screen flex">
			{/* Left Panel - Branding */}
			<div className="hidden lg:flex lg:w-[45%] bg-primary items-center justify-center p-12">
				<div className="w-full max-w-md space-y-8 text-primary-foreground">
					<HelmLogo size="small" color="hsl(0 0% 100%)" />

					<div className="space-y-6">
						<h3 className="text-xl font-semibold">Secure your account</h3>
						<p className="text-primary-foreground/80 text-[15px]">
							Create a strong password to protect your campaign data. Your
							password is encrypted and never stored in plain text.
						</p>
					</div>

					<div className="space-y-3 text-sm text-primary-foreground/60">
						<div className="flex items-center gap-2">
							<Lock className="size-4" />
							<span>Encrypted with bcrypt</span>
						</div>
						<div className="flex items-center gap-2">
							<ShieldCheck className="size-4" />
							<span>Industry-standard security</span>
						</div>
					</div>
				</div>
			</div>

			{/* Right Panel - Password Form */}
			<div className="flex-1 flex flex-col items-center justify-center px-6 py-12 lg:px-12">
				<div className="w-full max-w-md space-y-8">
					{/* Header */}
					<div className="space-y-2">
						<div className="flex items-center gap-2 text-primary">
							<KeyRound className="size-6" />
							<span className="text-sm font-medium uppercase tracking-wider">
								Step 2 of 3
							</span>
						</div>
						<h1 className="text-[32px] font-semibold tracking-tight text-foreground">
							Create Your Password
						</h1>
						<p className="text-[15px] text-muted-foreground">
							Choose a strong password with at least 8 characters.
						</p>
					</div>

					{/* Form */}
					<form onSubmit={handleSubmit} className="space-y-6">
						{/* Password field */}
						<div className="space-y-2">
							<Label htmlFor="password">Password</Label>
							<div className="relative">
								<Input
									id="password"
									type={showPassword ? "text" : "password"}
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									placeholder="Enter your password"
									className="pr-10"
									autoComplete="new-password"
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
									aria-label={showPassword ? "Hide password" : "Show password"}
								>
									{showPassword ? (
										<EyeOff className="size-4" />
									) : (
										<Eye className="size-4" />
									)}
								</button>
							</div>
						</div>

						{/* Strength indicator */}
						{password.length > 0 && (
							<div className="space-y-2">
								<div className="flex items-center justify-between text-xs">
									<span className="text-muted-foreground">
										Password strength
									</span>
									<span
										className={`font-medium ${
											strength.score <= 20
												? "text-red-500"
												: strength.score <= 50
													? "text-amber-500"
													: strength.score <= 75
														? "text-blue-500"
														: "text-green-500"
										}`}
									>
										{strength.label}
									</span>
								</div>
								<Progress
									value={strength.score}
									className={`h-1.5 ${strength.color.replace("bg-", "[&>[data-slot=progress-indicator]]:bg-")}`}
								/>
							</div>
						)}

						{/* Requirements checklist */}
						{password.length > 0 && (
							<div className="space-y-1.5">
								{requirementResults.map((req) => (
									<div
										key={req.label}
										className="flex items-center gap-2 text-sm"
									>
										{req.met ? (
											<Check className="size-4 text-green-500 shrink-0" />
										) : (
											<X className="size-4 text-muted-foreground/50 shrink-0" />
										)}
										<span
											className={
												req.met
													? "text-green-600 dark:text-green-400"
													: "text-muted-foreground"
											}
										>
											{req.label}
										</span>
									</div>
								))}
							</div>
						)}

						{/* Confirm password field */}
						<div className="space-y-2">
							<Label htmlFor="confirm-password">Confirm Password</Label>
							<div className="relative">
								<Input
									id="confirm-password"
									type={showConfirm ? "text" : "password"}
									value={confirmPassword}
									onChange={(e) => setConfirmPassword(e.target.value)}
									placeholder="Confirm your password"
									className="pr-10"
									autoComplete="new-password"
								/>
								<button
									type="button"
									onClick={() => setShowConfirm(!showConfirm)}
									className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
									aria-label={
										showConfirm
											? "Hide confirm password"
											: "Show confirm password"
									}
								>
									{showConfirm ? (
										<EyeOff className="size-4" />
									) : (
										<Eye className="size-4" />
									)}
								</button>
							</div>
							{confirmPassword.length > 0 && !passwordsMatch && (
								<p className="text-sm text-destructive">
									Passwords do not match
								</p>
							)}
							{passwordsMatch && (
								<p className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
									<Check className="size-3.5" />
									Passwords match
								</p>
							)}
						</div>

						{/* Error message */}
						{error && (
							<div className="rounded-md bg-destructive/10 border border-destructive/20 p-3">
								<p className="text-sm text-destructive">{error}</p>
							</div>
						)}

						{/* Submit button */}
						<Button
							type="submit"
							size="lg"
							className="w-full text-[15px] font-medium"
							disabled={!canSubmit}
						>
							{submitting ? "Creating Account..." : "Create Account"}
						</Button>
					</form>

					{/* Back link */}
					<div className="text-center">
						<Link
							to="/auth/accept-invite"
							search={{ token }}
							className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
						>
							<ArrowLeft className="size-3.5" />
							Back to authentication options
						</Link>
					</div>
				</div>
			</div>
		</div>
	)
}
